import React, { useState, useEffect } from 'react';

import { List, notification } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { showDateNoTime } from '@libs/NwMoment';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { useUpdateOptionNumber } from '@hooks/API/Event/UseEvent';

import AuthorAndDepartmentForEventCard from '@components/CreatedBy/AuthorAndDepartmentForEventCard';
import EventTypeTag from '@components/EventDetails/EventTypeTag';
import MiniLoading from '@components/Gui/MiniLoading';
import NwEllipsis from '@components/Gui/NwEllipsis';
import OptionSelector from '@components/OptionSelector/OptionSelector';
import OptionSelectorAllDates from '@components/OptionSelector/OptionSelectorAllDates';
import ProjectLine from '@components/ProjectLine/ProjectLine';
import { AgendaEventContent } from '@components/Schedule/Agenda/AgendaEventStyle';

const OptionPopoverContainer = styled.div`
    padding: 0;
    min-width: 21rem;

    .selected-date {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 1rem;

        div:first-child {
            min-width: 5rem;
        }
    }
    .all-dates {
        margin-top: 1rem;
        display: flex;
        justify-content: flex-start;
        align-items: center;

        div:first-child {
            min-width: 5rem;
        }
    }
    .other-events {
        margin-top: 1rem;
        padding-top: 1rem;
        border-top: ${(props) => props.theme.DefaultBorder};
        padding-bottom: 1rem;

        h2 {
            font-weight: 600;
            text-transform: uppercase;
            font-size: ${(props) => props.theme.FontSizeBase};
            border-bottom: ${(props) => props.theme.DefaultBorder};
            padding-bottom: 0.5rem;
            margin-bottom: 0.5rem;
        }
    }
`;

const EventDateModelOptionNumbers = ({
    eventID,
    projectID,
    eventDates,
    eventDateInfo,
    eventDatesModel,
    afterUpdate,
    selectedModel,
    onExtendOptionNumberToAllDates,
    showEvents,
}) => {
    const { mutateAsync: updateOptionNumber } = useUpdateOptionNumber();
    const [isLoading, setIsLoading] = useState(false);
    const [modelEvents, setModelEvents] = useState([]);
    const [isFetchingEvents, setIsFetchingEvents] = useState(false);

    const getEvents = async () => {
        setIsFetchingEvents(true);
        const dataToSend = {
            FDate: eventDateInfo.FromDate,
            TDate: eventDateInfo.ToDate,
            idmodels: [eventDatesModel.ModelID],
            inclJobs: true,
            inclExtJobs: true,
            inclCastings: true,
            inclTravels: true,
            inclAccommodations: true,
            inclExternalJobs: true,
            inclFollowUps: true,
            inclMeetings: true,
            inclNotices: true,
            checkConflict: true,
            General: false,
            Warning: false,
        };
        Axios.post('/calendar/getevents', dataToSend)
            .then((modelEventsInfo) => {
                const eventsWithoutCurrent = modelEventsInfo.Events.filter((event) => event.ID !== eventID);
                setModelEvents(eventsWithoutCurrent);
                setIsFetchingEvents(false);
            })
            .catch((error) => {
                notification.error({
                    message: 'Error fetching events',
                    description: error.message,
                });
                setIsFetchingEvents(false);
            });
    };

    useEffect(() => {
        if (showEvents) {
            getEvents();
        }
    }, [showEvents]);

    const onUpdateOptionNumber = async (optionNumber) => {
        setIsLoading(true);
        const data = {
            ModelID: eventDatesModel.ModelID,
            Eventid: eventID,
            EventDateModelID: eventDatesModel.ID,
            OptionNumber: optionNumber,
        };
        try {
            await updateOptionNumber({ ID: eventID, ProjectID: projectID, data });
            afterUpdate({
                ...eventDatesModel,
                ...data,
            });
        } catch (error) {
            console.error('Update Option Number Error => ', error);
        }
    };

    const handleExtendOptionNumber = async (optionNumber) => {
        setIsLoading(true);
        const data = {
            ModelID: eventDatesModel.ModelID,
            EventId: eventDatesModel.EventID,
            OptionNumber: optionNumber,
        };
        try {
            await updateOptionNumber({ ID: eventID, ProjectID: projectID, data });
            onExtendOptionNumberToAllDates(eventDatesModel.ModelID, eventDatesModel.OptionNumber);
        } catch (error) {
            console.error('Update Option Number Error => ', error);
        }
    };

    const eventModels = eventDates.filter((eventDate) => {
        if (eventDate.EventDatesModels.find((item) => item.ModelID === eventDatesModel.ModelID)) {
            return true;
        }
        return false;
    });

    return (
        <OptionPopoverContainer>
            {isLoading ? (
                <MiniLoading />
            ) : (
                <>
                    <div className="selected-date">
                        <div>{showDateNoTime(eventDateInfo.FromDate)}</div>
                        <div>
                            <OptionSelector
                                eventId={eventID}
                                modelId={selectedModel ? selectedModel.ModelID : null}
                                dates={[eventDatesModel.FromDate ? eventDatesModel.FromDate : eventDateInfo.FromDate]}
                                optionNumber={eventDatesModel.OptionNumber}
                                onUpdateOptionNumber={onUpdateOptionNumber}
                            />
                        </div>
                    </div>
                    {eventModels.length > 1 && (
                        <div className="all-dates">
                            <div>All dates</div>
                            <div>
                                <OptionSelectorAllDates onExtendOptionNumber={handleExtendOptionNumber} />
                            </div>
                        </div>
                    )}
                    {showEvents && (
                        <div className="other-events">
                            <h2>
                                Other Events for {getContactName(eventDatesModel.ModelID)} on{' '}
                                {showDateNoTime(eventDateInfo.FromDate)}
                            </h2>
                            {isFetchingEvents ? (
                                <MiniLoading />
                            ) : modelEvents.length > 0 ? (
                                <List
                                    size="small"
                                    itemLayout="horizontal"
                                    dataSource={modelEvents}
                                    renderItem={(event) => (
                                        <AgendaEventContent $cancel={event.Cancel}>
                                            {event.Project && <ProjectLine project={event.Project} ellipsis />}
                                            <EventTypeTag event={event} /> {event.Name}
                                            {event.Description && (
                                                <small>
                                                    <NwEllipsis text={event.Description} rows={2} popover />
                                                </small>
                                            )}
                                            {/* <small>
                                                {showDateNoTime(event.FromDate)}{" "}
                                                - {showDateNoTime(event.ToDate)}
                                            </small> */}
                                            <small style={{ marginTop: '0.5rem' }}>
                                                <AuthorAndDepartmentForEventCard object={event} />
                                            </small>
                                        </AgendaEventContent>
                                    )}
                                />
                            ) : (
                                <div>no other events</div>
                            )}
                        </div>
                    )}
                </>
            )}
        </OptionPopoverContainer>
    );
};

export default EventDateModelOptionNumbers;
